import { Dropdown, Menu, message, Tooltip, Popover } from 'antd';
import React, { useState } from 'react';
import { Redirect } from 'react-router';
import styled from 'styled-components';
import Cookies from 'js-cookie';
import {
    DeleteOutlined,
    ExclamationCircleOutlined,
    FolderAddOutlined,
    FolderOpenOutlined,
    GlobalOutlined,
    LinkOutlined,
    MoreOutlined,
    SyncOutlined,
    PlusOutlined,
    CommentOutlined,
    CopyOutlined,
    DownloadOutlined,
    LockOutlined,
    UnlockOutlined,
} from '@ant-design/icons';

import { AddOrRemoveGovernanceFlagModal } from './AddOrRemoveGovernanceFlagModal';
import { UpdateGovernanceStatusModal } from './UpdateGovernanceStatusModal';
import CreateGlossaryEntityModal from './CreateGlossaryEntityModal';
import { UpdateDeprecationModal } from './UpdateDeprecationModal';
import MoveGlossaryEntityModal from './MoveGlossaryEntityModal';
import BulkLoadEntityModal from './BulkLoadEntityModal';
import GlossaryCloneModal from './GlossaryCloneModal';
import DeleteDatasetModal from './DeleteDatasetModal';
import MoveDomainModal from './MoveDomainModal';

import { ENTITY_ACTIONS, BULK_IMPORT_PLATFORM_PRIVILEGE_MAP } from '../rbacConstantsService';
import { transformUrl, transformUrlToReactPath } from '../../../shared/url_utils/urlTransformer';
import { postMessageAction, postMessageService } from '../../../shared/postMessageService';
import { getEntityProfileDeleteRedirectPath } from '../../../shared/deleteUtils';
import useDropdownKeyboard from '../../../hooks/useDropdownKeyboard';
import { useTranslation } from '../../../../i18n/useTranslation';
import { CLIENT_AUTH_COOKIE } from '../../../../conf/Global';
import { getPermissionTooltips } from '../rbacUtilsService';
import useDeleteEntity from './useDeleteEntity';
import { ANTD_GRAY } from '../constants';
import { useRbac } from '../useRbac';

import { useUpdateDeprecationMutation, useUpdateReadOnlyMutation } from '../../../../graphql/mutations.generated';
import { useCheckModuleEnabledQuery } from '../../../../graphql/app.generated';
import { useGetCommentsQuery } from '../../../../graphql/user.generated';
import { EntityType } from '../../../../types.generated';

export enum EntityMenuItems {
    COPY_URL,
    UPDATE_DEPRECATION,
    ADD_TERM,
    ADD_TERM_GROUP,
    EXPORT_TERM,
    EXPORT_NODE,
    DELETE,
    BULK_IMPORT,
    MOVE,
    DELETE_DATASET,
    GOVERNANCE_STATUS,
    GOVERNANCE_FLAG,
    ADD_COMMENT,
    MARK_AS_READ_ONLY,
    CLONE,
}

export const MenuIcon = styled(MoreOutlined)<{ fontSize?: number }>`
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: ${(props) => props.fontSize || '24'}px;
    height: 32px;
    margin-left: 5px;

    svg {
        width: 24px;
        height: 24px;
    }
`;

const MenuItem = styled.div`
    padding: 0 4px;
    color: #262626;
`;

const StyledMenu = styled(Menu)`
    min-width: 140px !important;
`;

const StyledMenuItem = styled(Menu.Item)<{ disabled: boolean }>`
    ${(props) =>
        props.disabled
            ? `
            ${MenuItem} {
                color: ${ANTD_GRAY[7]};
            }
    `
            : ''}
`;

interface ReadOnlyPopoverProps {
    isReadOnly: boolean;
    children: React.ReactNode;
    t: (key: string) => string;
}

const ReadOnlyPopover = ({ isReadOnly, children, t }: ReadOnlyPopoverProps) => {
    return (
        <Popover
            content={isReadOnly ? t('ASSET_MARKED_AS_READ_ONLY') : null}
            placement="left"
            trigger="hover"
            overlayStyle={isReadOnly ? { pointerEvents: 'auto', zIndex: 1060 } : { display: 'none' }}
        >
            <span style={{ pointerEvents: isReadOnly ? 'auto' : 'inherit' }}>{children}</span>
        </Popover>
    );
};

interface Options {
    hideDeleteMessage?: boolean;
    skipDeleteWait?: boolean;
}

interface Props {
    urn: string;
    entityType: EntityType;
    entityData?: any;
    menuItems: Set<EntityMenuItems>;
    size?: number;
    options?: Options;
    onExportClicked?: () => void;
    refetchForEntity?: () => void;
    refetchForTerms?: () => void;
    refetchForNodes?: () => void;
    onDeleteEntity?: () => void;
    // eslint-disable-next-line react/no-unused-prop-types
    onDeleteDataset?: () => void;
}

type EntityMenuWrapperProps = {
    /** Tooltip text when item is disabled or unauthorized; no tooltip when undefined */
    title?: string;
} & React.ComponentProps<typeof Menu.Item>;

function EntityMenuWrapper({ title, ...menuItemProps }: EntityMenuWrapperProps) {
    const content = <StyledMenuItem disabled={menuItemProps.disabled ?? false} {...menuItemProps} />;
    if (title) {
        return (
            <Tooltip title={title} placement="left" overlayClassName="f-tooltip-white">
                <span>{content}</span>
            </Tooltip>
        );
    }
    return content;
}

function EntityDropdown(props: Props) {
    const { t } = useTranslation();
    const {
        urn,
        entityData,
        entityType,
        menuItems,
        refetchForEntity,
        refetchForTerms,
        refetchForNodes,
        onDeleteEntity: onDelete,
        size,
        options,
        onExportClicked,
        onDeleteDataset,
    } = props;

    const actorUrn = Cookies.get(CLIENT_AUTH_COOKIE) || '';
    const { canPerformEntityMenuAction, platformPrivileges } = useRbac();

    const { data, loading } = useGetCommentsQuery({ variables: { input: { entity: urn } } });
    const { data: moduleInfo } = useCheckModuleEnabledQuery({ variables: {} });
    const [updateDeprecation] = useUpdateDeprecationMutation();
    const [updateReadOnly] = useUpdateReadOnlyMutation();
    const { onDeleteEntity, hasBeenDeleted } = useDeleteEntity(
        urn,
        entityType,
        entityData,
        onDelete,
        options?.hideDeleteMessage,
        options?.skipDeleteWait,
    );
    const [isDeleteDatasetModalVisible, setIsDeleteDatasetModalVisible] = useState(false);
    const [isGovernanceFlagModalVisible, setIsGovernanceFlagModalVisible] = useState(false);
    const [isGlossaryCloneModalVisible, setIsGlossaryCloneModalVisible] = useState(false);
    const [isDeprecationModalVisible, setIsDeprecationModalVisible] = useState(false);
    const [isCreateTermModalVisible, setIsCreateTermModalVisible] = useState(false);
    const [isCreateNodeModalVisible, setIsCreateNodeModalVisible] = useState(false);
    const [isBulkImportModalVisible, setIsBulkImportModalVisible] = useState(false);
    const [isGovernanceModalVisible, setIsGovernanceModalVisible] = useState(false);
    const [isMoveModalVisible, setIsMoveModalVisible] = useState(false);
    const { visible, setVisible, handleKeyDown, toggleDropdown } = useDropdownKeyboard();

    const getPrivileges = (action) => {
        return canPerformEntityMenuAction(entityType, action);
    };

    const canAddTermGroup = getPrivileges(ENTITY_ACTIONS.ADD_TERM_GROUP);
    const canDeleteEntity = getPrivileges(ENTITY_ACTIONS.DELETE);
    const canAddTerm = getPrivileges(ENTITY_ACTIONS.ADD_TERM);
    const canClone = getPrivileges(ENTITY_ACTIONS.CLONE);
    const canUpdateGovStatus = getPrivileges(ENTITY_ACTIONS.UPDATE_GOV_STATUS);
    const canUpdateGovFlag = getPrivileges(ENTITY_ACTIONS.UPDATE_GOV_FLAG_STATUS);
    const canEditReadOnly = getPrivileges(ENTITY_ACTIONS.EDIT_READONLY);
    const canEditDeprecation = getPrivileges(ENTITY_ACTIONS.EDIT_DEPRECATION);
    const canMove = getPrivileges(ENTITY_ACTIONS.UPDATE_PARENT);
    const canExport = getPrivileges(ENTITY_ACTIONS.EXPORT);
    const canBulkImport = platformPrivileges?.[BULK_IMPORT_PLATFORM_PRIVILEGE_MAP[entityType]];

    // Transforms a permission map into a tooltip map.
    const tooltips = getPermissionTooltips(t, {
        export: canExport,
        clone: canClone,
        delete: canDeleteEntity,
        govStatus: canUpdateGovStatus,
        govFlag: canUpdateGovFlag,
        readOnly: canEditReadOnly,
        deprecation: canEditDeprecation,
        move: canMove,
        addTerm: canAddTerm,
        addTermGroup: canAddTermGroup,
        bulkImport: canBulkImport,
    });

    const isMessageCenterEnabled = moduleInfo?.checkModuleEnabled?.isEnabled;

    // Only disable when explicitly set to true (null and false are treated as enabled)
    const isReadOnly = entityData?.readOnly?.readOnly ?? false;

    const closeDropdown = () => {
        setVisible(false);
    };

    const handleUpdateDeprecation = async (deprecatedStatus: boolean) => {
        message.loading({ content: t('Updating') });
        try {
            await updateDeprecation({
                variables: {
                    input: {
                        urn,
                        deprecated: deprecatedStatus,
                        note: '',
                        decommissionTime: null,
                    },
                },
            });
            message.destroy();
            message.success({ content: t('Deprecation Updated'), duration: 2 });
        } catch (e: unknown) {
            message.destroy();
            if (e instanceof Error) {
                message.error({ content: `${t('Failed to update Deprecation:')} \n ${e.message || ''}`, duration: 2 });
            }
        }
        refetchForEntity?.();
    };

    const handleUpdateReadOnly = async (readOnlyStatus: boolean) => {
        message.loading({ content: t('Updating') });
        try {
            await updateReadOnly({
                variables: {
                    input: {
                        urn,
                        readOnly: readOnlyStatus,
                    },
                },
            });
            message.destroy();
            message.success({ content: t('Read only status updated'), duration: 2 });
        } catch (e: unknown) {
            message.destroy();
            if (e instanceof Error) {
                message.error({
                    content: `${t('Failed to update read only status:')} \n ${e.message || ''}`,
                    duration: 2,
                });
            }
        }
        refetchForEntity?.();
    };

    const pageUrl = window.location.href;
    const isGlossaryEntity = entityType === EntityType.GlossaryNode || entityType === EntityType.GlossaryTerm;
    const isDomainEntity = entityType === EntityType.Domain;

    /**
     * A default path to redirect to if the entity is deleted.
     */
    const deleteRedirectPath = getEntityProfileDeleteRedirectPath(entityType, entityData);

    // This method checks if current user has commented in the dataset or not
    const hasUserCommented = () => {
        return data?.getComments?.users?.includes(actorUrn);
    };

    const handleButtonClick = () => {
        postMessageService.sendToParent({
            action: postMessageAction.OPEN_NAVIGATION_MODAL,
            urn,
            isTempChannel: !hasUserCommented(),
        });
    };

    return (
        <>
            <Dropdown
                open={visible}
                overlay={
                    <StyledMenu>
                        {menuItems.has(EntityMenuItems.COPY_URL) && navigator.clipboard && (
                            <Menu.Item key="0">
                                <MenuItem
                                    aria-label={t('Copy Link')}
                                    onClick={() => {
                                        let transformedUrl = '';
                                        if (entityType === EntityType.Tag) {
                                            const tagUrl = `/tag/${urn}`;
                                            transformedUrl = transformUrlToReactPath(tagUrl);
                                        } else {
                                            transformedUrl = transformUrl(pageUrl);
                                        }
                                        navigator.clipboard.writeText(transformedUrl);
                                        message.info(t('Copied URL!'), 1.2);
                                        closeDropdown();
                                    }}
                                >
                                    <LinkOutlined /> &nbsp; {t('Copy Link')}
                                </MenuItem>
                            </Menu.Item>
                        )}
                        {menuItems.has(EntityMenuItems.UPDATE_DEPRECATION) && (
                            <EntityMenuWrapper
                                key="1"
                                title={tooltips.deprecation}
                                disabled={isReadOnly || !canEditDeprecation}
                                onClick={
                                    !entityData?.deprecation?.deprecated
                                        ? () => {
                                              setIsDeprecationModalVisible(true);
                                              closeDropdown();
                                          }
                                        : () => {
                                              handleUpdateDeprecation(false);
                                              closeDropdown();
                                          }
                                }
                            >
                                {!entityData?.deprecation?.deprecated ? (
                                    <MenuItem aria-label={t('Mark as deprecated')}>
                                        <ReadOnlyPopover isReadOnly={isReadOnly && canEditDeprecation} t={t}>
                                            <ExclamationCircleOutlined /> &nbsp; {t('Mark as deprecated')}
                                        </ReadOnlyPopover>
                                    </MenuItem>
                                ) : (
                                    <MenuItem aria-label={t('Mark as un-deprecated')}>
                                        <ReadOnlyPopover isReadOnly={isReadOnly && canEditDeprecation} t={t}>
                                            <ExclamationCircleOutlined /> &nbsp; {t('Mark as un-deprecated')}
                                        </ReadOnlyPopover>
                                    </MenuItem>
                                )}
                            </EntityMenuWrapper>
                        )}
                        {menuItems.has(EntityMenuItems.ADD_TERM) && (
                            <EntityMenuWrapper
                                key="2"
                                title={tooltips.addTerm}
                                disabled={!canAddTerm || isReadOnly}
                                onClick={() => {
                                    setIsCreateTermModalVisible(true);
                                    closeDropdown();
                                }}
                                aria-label={t('Add Term')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly && canAddTerm} t={t}>
                                        <PlusOutlined aria-hidden="true" /> &nbsp;{t('Add Term')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </EntityMenuWrapper>
                        )}
                        {menuItems.has(EntityMenuItems.ADD_TERM_GROUP) && (
                            <EntityMenuWrapper
                                key="3"
                                title={tooltips.addTermGroup}
                                disabled={!canAddTermGroup || isReadOnly}
                                onClick={() => {
                                    setIsCreateNodeModalVisible(true);
                                    closeDropdown();
                                }}
                                aria-label={t('Add Term Group')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly && canAddTermGroup} t={t}>
                                        <FolderAddOutlined /> &nbsp;{t('Add Term Group')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </EntityMenuWrapper>
                        )}
                        {menuItems.has(EntityMenuItems.BULK_IMPORT) && (
                            <EntityMenuWrapper
                                data-testid="entity-menu-move-button"
                                key="4"
                                title={tooltips.bulkImport}
                                disabled={!canBulkImport || isReadOnly}
                                onClick={() => {
                                    setIsBulkImportModalVisible(true);
                                    closeDropdown();
                                }}
                                aria-label={t('Bulk Import')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly && !!canBulkImport} t={t}>
                                        <PlusOutlined aria-hidden="true" /> &nbsp;{t('Bulk Import')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </EntityMenuWrapper>
                        )}
                        {menuItems.has(EntityMenuItems.MOVE) && (
                            <EntityMenuWrapper
                                key="5"
                                title={tooltips.move}
                                disabled={!canMove || isReadOnly}
                                onClick={() => {
                                    setIsMoveModalVisible(true);
                                    closeDropdown();
                                }}
                                aria-label={t('Move')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly && canMove} t={t}>
                                        <FolderOpenOutlined /> &nbsp;{t('Move')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </EntityMenuWrapper>
                        )}
                        {menuItems.has(EntityMenuItems.DELETE) && (
                            <EntityMenuWrapper
                                key="6"
                                title={tooltips.delete}
                                disabled={
                                    !canDeleteEntity ||
                                    // isDeleteDisabled(entityType, entityData, me.platformPrivileges) ||
                                    isReadOnly
                                }
                                onClick={() => {
                                    onDeleteEntity();
                                    closeDropdown();
                                }}
                                aria-label={t('Delete')}
                            >
                                <MenuItem data-testid="entity-menu-delete-button">
                                    <ReadOnlyPopover isReadOnly={isReadOnly && canDeleteEntity} t={t}>
                                        <DeleteOutlined /> &nbsp;{t('Delete')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </EntityMenuWrapper>
                        )}
                        {menuItems.has(EntityMenuItems.DELETE_DATASET) && (
                            <EntityMenuWrapper
                                key="7"
                                title={tooltips.delete}
                                disabled={!canDeleteEntity || isReadOnly}
                                onClick={() => setIsDeleteDatasetModalVisible(true)}
                                aria-label={t('Delete Dataset')}
                            >
                                <MenuItem onClick={onDeleteDataset}>
                                    <ReadOnlyPopover isReadOnly={isReadOnly && canDeleteEntity} t={t}>
                                        <DeleteOutlined /> &nbsp;{t('Delete Dataset')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </EntityMenuWrapper>
                        )}
                        {menuItems.has(EntityMenuItems.GOVERNANCE_STATUS) && (
                            <EntityMenuWrapper
                                key="8"
                                title={tooltips.govStatus}
                                disabled={!canUpdateGovStatus || isReadOnly}
                                onClick={() => {
                                    setIsGovernanceModalVisible(true);
                                    closeDropdown();
                                }}
                                aria-label={t('Update Governance Status')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly && canUpdateGovStatus} t={t}>
                                        <SyncOutlined /> &nbsp;{t('Update Governance Status')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </EntityMenuWrapper>
                        )}
                        {menuItems.has(EntityMenuItems.GOVERNANCE_FLAG) && (
                            <EntityMenuWrapper
                                key="9"
                                title={tooltips.govFlag}
                                disabled={!canUpdateGovFlag || isReadOnly}
                                onClick={() => {
                                    setIsGovernanceFlagModalVisible(true);
                                    closeDropdown();
                                }}
                                aria-label={t('Add Governance Flag')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly && canUpdateGovFlag} t={t}>
                                        <GlobalOutlined /> &nbsp;{t('Add Governance Flag')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </EntityMenuWrapper>
                        )}
                        {isMessageCenterEnabled && menuItems.has(EntityMenuItems.ADD_COMMENT) && (
                            <StyledMenuItem
                                key="10"
                                disabled={loading}
                                onClick={() => {
                                    handleButtonClick();
                                    closeDropdown();
                                }}
                                aria-label={t('Comment')}
                            >
                                <MenuItem>
                                    <CommentOutlined /> &nbsp;{t('Comment')}
                                </MenuItem>
                            </StyledMenuItem>
                        )}
                        {menuItems.has(EntityMenuItems.MARK_AS_READ_ONLY) && (
                            <EntityMenuWrapper
                                key="11"
                                title={tooltips.readOnly}
                                disabled={!canEditReadOnly}
                                onClick={
                                    !entityData?.readOnly?.readOnly
                                        ? () => handleUpdateReadOnly(true)
                                        : () => handleUpdateReadOnly(false)
                                }
                                aria-label={
                                    !entityData?.readOnly?.readOnly ? t('Mark as read only') : t('Mark as editable')
                                }
                            >
                                {!entityData?.readOnly?.readOnly ? (
                                    <MenuItem>
                                        <LockOutlined /> &nbsp;{t('Mark as read only')}
                                    </MenuItem>
                                ) : (
                                    <MenuItem>
                                        <UnlockOutlined /> &nbsp;{t('Mark as editable')}
                                    </MenuItem>
                                )}
                            </EntityMenuWrapper>
                        )}
                        {menuItems.has(EntityMenuItems.CLONE) && (
                            <EntityMenuWrapper
                                key="12"
                                title={tooltips.clone}
                                disabled={!canClone || isReadOnly}
                                onClick={() => {
                                    setIsGlossaryCloneModalVisible(true);
                                    closeDropdown();
                                }}
                                aria-label={t('Clone')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly && canClone} t={t}>
                                        <CopyOutlined />
                                        &nbsp;{t('Clone')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </EntityMenuWrapper>
                        )}

                        {menuItems.has(EntityMenuItems.EXPORT_TERM) && (
                            <EntityMenuWrapper
                                key="12"
                                title={tooltips.export}
                                disabled={loading || isReadOnly || !canExport}
                                onClick={() => {
                                    onExportClicked?.();
                                    closeDropdown();
                                }}
                                aria-label={t('Export')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly && canExport} t={t}>
                                        <DownloadOutlined style={{ width: 16 }} color="#5F767C" /> &nbsp;
                                        {t('Export')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </EntityMenuWrapper>
                        )}
                        {menuItems.has(EntityMenuItems.EXPORT_NODE) && (
                            <StyledMenuItem
                                key="14"
                                disabled={loading || isReadOnly || !canExport}
                                onClick={() => {
                                    onExportClicked?.();
                                    closeDropdown();
                                }}
                                aria-label={t('Export All')}
                            >
                                <MenuItem>
                                    <ReadOnlyPopover isReadOnly={isReadOnly && canExport} t={t}>
                                        <DownloadOutlined style={{ width: 16 }} color="#5F767C" /> &nbsp;
                                        {t('Export All')}
                                    </ReadOnlyPopover>
                                </MenuItem>
                            </StyledMenuItem>
                        )}
                    </StyledMenu>
                }
                trigger={['click']}
                onOpenChange={(flag) => setVisible(flag)}
            >
                <MenuIcon
                    tabIndex={0}
                    fontSize={size}
                    aria-haspopup="true"
                    aria-label={t('More options')}
                    onClick={toggleDropdown}
                    onKeyDown={handleKeyDown}
                    data-testid="entity-header-dropdown"
                />
            </Dropdown>
            {isCreateTermModalVisible && (
                <CreateGlossaryEntityModal
                    entityType={EntityType.GlossaryTerm}
                    onClose={() => setIsCreateTermModalVisible(false)}
                    refetchData={refetchForTerms}
                />
            )}
            {isCreateNodeModalVisible && (
                <CreateGlossaryEntityModal
                    entityType={EntityType.GlossaryNode}
                    onClose={() => setIsCreateNodeModalVisible(false)}
                    refetchData={refetchForNodes}
                />
            )}
            {isDeprecationModalVisible && (
                <UpdateDeprecationModal
                    urns={[urn]}
                    onClose={() => setIsDeprecationModalVisible(false)}
                    refetch={refetchForEntity}
                />
            )}
            {isBulkImportModalVisible && (
                <BulkLoadEntityModal
                    entityData={entityData}
                    entityType={EntityType.GlossaryTerm}
                    onClose={() => setIsBulkImportModalVisible(false)}
                />
            )}
            {isMoveModalVisible && isGlossaryEntity && (
                <MoveGlossaryEntityModal onClose={() => setIsMoveModalVisible(false)} />
            )}

            {isDeleteDatasetModalVisible && (
                <DeleteDatasetModal urn={urn} onClose={() => setIsDeleteDatasetModalVisible(false)} />
            )}

            {isGovernanceModalVisible && (
                <UpdateGovernanceStatusModal
                    entityData={entityData}
                    refetch={refetchForEntity}
                    onClose={() => setIsGovernanceModalVisible(false)}
                />
            )}
            {isMoveModalVisible && isDomainEntity && <MoveDomainModal onClose={() => setIsMoveModalVisible(false)} />}

            {isGovernanceFlagModalVisible && (
                <AddOrRemoveGovernanceFlagModal
                    entityData={entityData}
                    refetch={refetchForEntity}
                    onClose={() => setIsGovernanceFlagModalVisible(false)}
                />
            )}
            {isGlossaryCloneModalVisible && (
                <GlossaryCloneModal
                    glossaryName={entityData?.properties?.name || ''}
                    entityType={EntityType.GlossaryTerm}
                    onClose={() => setIsGlossaryCloneModalVisible(false)}
                />
            )}
            {hasBeenDeleted && !onDelete && deleteRedirectPath && <Redirect to={deleteRedirectPath} />}
        </>
    );
}

export default EntityDropdown;
